'use client';

import { useEffect, useState } from 'react';

import Link from 'next/link';

import { ExternalLink, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { FeedbackForm } from '@kit/feedback';
import { useUser } from '@kit/supabase/hooks/use-user';
import {
  ResponsivePopup,
  ResponsivePopupContent,
  ResponsivePopupTrigger,
} from '@kit/ui/responsive-popup';
import { cn } from '@kit/ui/utils';

import { UkFlag } from '~/components/flags';
import appConfig from '~/config/app.config';
import { useDatabaseStats } from '~/lib/contexts/database-stats-context';

function formatSyncDate(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const date = d.toLocaleDateString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Warsaw',
  });
  const time = d.toLocaleTimeString('pl-PL', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Warsaw',
  });
  return { date, time };
}

function formatCount(n: number): string {
  return n.toLocaleString('pl-PL');
}

interface SyncBarFixedProps {
  isViewerOpen?: boolean;
  panelWidth?: number;
}

export function SyncBarFixed({
  isViewerOpen = false,
  panelWidth = 50,
}: SyncBarFixedProps) {
  const stats = useDatabaseStats();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const { data: user } = useUser();
  const { t } = useTranslation(['common', 'marketing']);

  useEffect(() => {
    const bottomThreshold = 20;

    const isAtBottom = () =>
      window.innerHeight + window.scrollY >=
      document.documentElement.scrollHeight - bottomThreshold;

    const isPageScrollable = () =>
      document.documentElement.scrollHeight >
      window.innerHeight + bottomThreshold;

    // Scrollable pages: only show when actually at the bottom
    // Short pages: footer stays hidden until user explicitly scrolls (wheel/touch)
    const onScroll = () => {
      if (!isPageScrollable()) return;
      setShowFooter(isAtBottom());
    };

    // Short pages (can't scroll): show/hide on wheel direction
    const onWheel = (e: WheelEvent) => {
      if (isPageScrollable()) return;
      if (e.deltaY > 0) {
        setShowFooter(true);
      } else if (e.deltaY < 0) {
        setShowFooter(false);
      }
    };

    let lastTouchY = 0;
    const onTouchStart = (e: TouchEvent) => {
      lastTouchY = e.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (isPageScrollable()) return;
      const currentY = e.touches[0]?.clientY ?? 0;
      if (currentY < lastTouchY) {
        setShowFooter(true);
      } else if (currentY > lastTouchY) {
        setShowFooter(false);
      }
      lastTouchY = currentY;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, []);

  if (!stats) return null;

  const { date, time } = stats.lastSyncAt
    ? formatSyncDate(stats.lastSyncAt)
    : { date: null, time: null };

  return (
    <div
      className={cn(
        '@container fixed inset-x-0 bottom-0 z-40 bg-stone-800 text-[11px] text-stone-400 dark:bg-zinc-900 dark:text-zinc-500',
        isViewerOpen && 'lg:[right:var(--panel-width)]',
      )}
      style={
        isViewerOpen
          ? ({ '--panel-width': `${panelWidth}%` } as React.CSSProperties)
          : undefined
      }
    >
      {/* Sync info row */}
      <div className="flex items-center justify-center gap-2 px-3 py-3 @[640px]:gap-2.5 @[640px]:px-4">
        <Link
          href="/historia-synchronizacji"
          className="group inline-flex items-center gap-1.5 rounded-sm px-1.5 py-0.5 transition-all hover:bg-stone-700/60 hover:text-stone-200 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
        >
          {/* Green pulsing dot */}
          <span className="relative inline-flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-40 transition-opacity group-hover:opacity-75" />
            <span className="relative inline-flex h-full w-full rounded-full bg-emerald-400 shadow-[0_0_4px_1px_rgba(52,211,153,0.45)] transition-shadow group-hover:shadow-[0_0_6px_2px_rgba(52,211,153,0.6)]" />
          </span>

          {/* Phone (<640px container): room for the feedback button */}
          {date && (
            <span className="whitespace-nowrap @[640px]:hidden">{date}</span>
          )}

          {/* Medium (640–1024px container): short name, date + time */}
          {date && (
            <span className="hidden @[640px]:inline @[1024px]:hidden">
              Zsynchronizowano z{' '}
              <span className="text-stone-300 dark:text-zinc-300">RPL</span>{' '}
              {date}, {time}
            </span>
          )}

          {/* Large (≥1024px container): full name, date + time */}
          {date && (
            <span className="hidden @[1024px]:inline">
              Zsynchronizowano z{' '}
              <span className="text-stone-300 dark:text-zinc-300">
                Rejestrem Produktów Leczniczych
              </span>{' '}
              {date}, {time}
            </span>
          )}
          <span className="hidden text-stone-600 group-hover:text-stone-500 @[360px]:inline dark:text-zinc-700 dark:group-hover:text-zinc-600">
            |
          </span>
          <span className="hidden whitespace-nowrap @[360px]:inline">
            {formatCount(stats.extractedCount)} leków
          </span>
        </Link>

        <span className="text-stone-600 dark:text-zinc-700">|</span>

        <ResponsivePopup
          desktop="popover"
          open={feedbackOpen}
          onOpenChange={setFeedbackOpen}
        >
          <ResponsivePopupTrigger asChild>
            <button
              type="button"
              data-test="feedback-trigger"
              className="inline-flex items-center gap-1 rounded-md border-2 border-stone-600 bg-stone-700 px-2 py-1 text-stone-200 shadow-[2px_2px_0_0_#44403c] transition-all hover:-translate-y-0.5 hover:border-stone-500 hover:bg-stone-600 hover:text-stone-200 hover:shadow-[3px_3px_0_0_#44403c] @[768px]:py-0.5 @[768px]:text-stone-400"
            >
              <MessageCircle className="h-3 w-3" />
              <span className="text-[11px] @[768px]:hidden">Opinia</span>
              <span className="hidden text-[10px] @[768px]:inline">
                Prześlij opinię
              </span>
            </button>
          </ResponsivePopupTrigger>
          <ResponsivePopupContent
            srTitle="Prześlij opinię"
            popoverProps={{
              side: 'top',
              align: 'end',
              sideOffset: 12,
              collisionPadding: 20,
            }}
            desktopClassName="w-96"
          >
            <FeedbackForm
              onClose={() => setFeedbackOpen(false)}
              hideEmail={!!user}
            />
          </ResponsivePopupContent>
        </ResponsivePopup>

        <span className="text-stone-600 dark:text-zinc-700">|</span>

        {/* Plain anchor (full reload): the server picks the language from the path */}
        <a
          href="/en"
          lang="en"
          title="English demo"
          aria-label="English demo"
          className="inline-flex items-center gap-1.5 rounded-sm px-1.5 py-0.5 transition-all hover:bg-stone-700/60 hover:text-stone-200 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
        >
          <UkFlag className="h-2.5 w-[15px] border border-stone-500" />
          <span className="@[768px]:hidden">EN</span>
          <span className="hidden @[768px]:inline">English demo</span>
        </a>
      </div>

      {/* Footer reveal row — slides open when scrolled to bottom */}
      <div
        className={cn(
          'grid transition-all duration-300 ease-in-out',
          showFooter
            ? 'grid-rows-[1fr] opacity-100'
            : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div className="overflow-hidden">
          <div className="flex items-center justify-center gap-1 border-t border-stone-700 px-4 pt-2 pb-2.5 text-[10px] text-stone-400 @[768px]:text-xs dark:border-zinc-800 dark:text-zinc-500">
            {t('marketing:copyright', {
              year: new Date().getFullYear(),
              product: appConfig.name,
            })}
            <span aria-hidden="true" className="mx-1">
              ·
            </span>
            {t('common:madeIn')}
            <span aria-hidden="true" className="mx-1">
              ·
            </span>
            <Link
              href="/contact"
              className="underline decoration-current/50 transition-all hover:text-stone-200 hover:decoration-current/80 dark:hover:text-zinc-300 dark:hover:decoration-current/80"
            >
              kontakt
            </Link>
            <span aria-hidden="true" className="mx-1">
              ·
            </span>
            <a
              href="/zasady.txt"
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-0.5 underline decoration-current/50 transition-all hover:text-stone-200 hover:decoration-current/80 dark:hover:text-zinc-300 dark:hover:decoration-current/80"
            >
              zasady.txt
              <ExternalLink className="h-2.5 w-2.5" aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
